// VWO Context for client-side flag decisions using VWO FME React SDK
// Provides real-time decisions to React components.

import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { VWOProvider as VWOFMEProvider, useGetFlag } from "vwo-fme-react-sdk";
import { getVWOFMEUserId } from "../utils/vwoApplyTest";

// Conditionally import useTrackEvent to avoid SSR issues
let useTrackEvent;
if (typeof window !== 'undefined') {
  try {
    ({ useTrackEvent } = require("vwo-fme-react-sdk"));
  } catch (error) {
    console.warn('Failed to load useTrackEvent:', error);
    useTrackEvent = () => ({ trackEvent: null, isReady: false });
  }
} else {
  // SSR fallback
  useTrackEvent = () => ({ trackEvent: null, isReady: false });
}

const DEBUG = typeof window !== "undefined" && (window.location.search.includes("vwo_debug=1") || process.env.GATSBY_DEBUG_VWO === "true");

const FLAG_KEY = "applyNowFormInterstitial";
const VAR_SHOW_INTERSTITIAL = "show_interstitial";

// Property whitelist now handled at build-time in gatsby-node.js

// --- Identity helpers -------------------------------------------------------

function readVwoUuidCookie() {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)_vwo_uuid=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/**
 * Wait up to `timeoutMs` for SmartCode to set _vwo_uuid.
 * Returns the UUID or null if it didn't appear in time.
 */
function waitForVwoUuid(timeoutMs = 1500, intervalMs = 50) {
  return new Promise((resolve) => {
    const existing = readVwoUuidCookie();
    if (existing) return resolve(existing);

    const start = performance.now();
    const t = setInterval(() => {
      const id = readVwoUuidCookie();
      if (id) {
        clearInterval(t);
        resolve(id);
      } else if (performance.now() - start > timeoutMs) {
        clearInterval(t);
        resolve(null);
      }
    }, intervalMs);
  });
}

// --- Decision context --------------------------------------------------------

const VWOApplyContext = createContext({
  variation: "control",
  flagEnabled: false,
  showInterstitial: false,
  ready: false,        // <- simpler than isLoading
  isLoading: true,     // kept for compatibility
  error: null,
});

const VWOApplyTestProvider = React.memo(({ children }) => {
  const [userContext, setUserContext] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  if (DEBUG) {
    console.log("🎯 VWOApplyTestProvider rendered:", { userContext: !!userContext, isInitialized });
  }

  // Initialize user context with VWO UUID
  useEffect(() => {
    let cancelled = false;

    async function initializeUserContext() {
      // Use separate FME UUID for clean separation from SmartCode
      const userId = getVWOFMEUserId();
      
      if (!cancelled) {
        const context = { id: userId };
        setUserContext(context);
        setIsInitialized(true);
        
        if (DEBUG) {
          console.log("🎯 VWO FME: User context initialized with separate FME UUID:", context);
          console.log("🎯 VWO FME Visitor Registration:", {
            fmeUserId: userId,
            flagKey: FLAG_KEY,
            userContextId: context.id
          });
        }
      }
    }

    initializeUserContext();

    return () => {
      cancelled = true;
    };
  }, []);

  // Use the useGetFlag hook with a memoized default user context to prevent errors
  const safeUserContext = useMemo(() => {
    return userContext || { id: 'loading' };
  }, [userContext]);
  
  const { flag, isReady } = useGetFlag(FLAG_KEY, safeUserContext);

  const value = useMemo(() => {
    if (DEBUG) {
      console.log("🎯 VWO Context value recalculated:", { 
        isReady, 
        isInitialized, 
        userContext: !!userContext,
        safeUserContextId: safeUserContext?.id 
      });
    }
    
    if (!isReady || !isInitialized || !userContext) {
      return {
        variation: "control",
        flagEnabled: false,
        showInterstitial: false,
        ready: false,
        isLoading: true,
        error: null,
      };
    }

    try {
      const flagEnabled = !!flag?.isEnabled();
      const showInterstitial = !!flag?.getVariable(VAR_SHOW_INTERSTITIAL, false);
      const variation = flagEnabled && showInterstitial ? "treatment" : "control";

      if (DEBUG) {
        console.log("🎯 VWO FME Decision:", { 
          userContext: safeUserContext, 
          flagEnabled, 
          showInterstitial, 
          variation 
        });
        console.log(`🎯 VWO FME Final Decision: ${variation.toUpperCase()} - ${variation === 'treatment' ? 'SHOW INTERSTITIAL' : 'DIRECT REDIRECT'}`);
      }

      return {
        variation,
        flagEnabled,
        showInterstitial,
        ready: true,
        isLoading: false,
        error: null,
      };
    } catch (err) {
      if (DEBUG) console.error("❌ VWO FME Error:", err);
      
      return {
        variation: "control",
        flagEnabled: false,
        showInterstitial: false,
        ready: true,
        isLoading: false,
        error: err?.message || String(err),
      };
    }
  }, [flag, isReady, isInitialized, safeUserContext]);

  return <VWOApplyContext.Provider value={value}>{children}</VWOApplyContext.Provider>;
});

// --- Bypass provider for non-whitelisted properties ------------------------

const VWOBypassProvider = ({ children, pageContext }) => {
  const value = useMemo(() => ({
    variation: "not_in_test",
    flagEnabled: false,
    showInterstitial: false,
    ready: true,
    isLoading: false,
    error: null,
  }), []);

  if (DEBUG) {
    console.log("🎯 VWO: Property not whitelisted (build-time), bypassing VWO entirely", {
      property: pageContext?.slug || 'unknown',
      isVWOEnabled: pageContext?.isVWOEnabled
    });
  }

  return <VWOApplyContext.Provider value={value}>{children}</VWOApplyContext.Provider>;
};

// --- Top-level provider (mount once in wrapRootElement) ----------------------

export const VWOProvider = ({ children, pageContext = {} }) => {
  // Use build-time pageContext to determine VWO status (no hydration mismatch)
  const isWhitelisted = pageContext.isVWOEnabled ?? true; // Default to enabled for non-community pages
  
  // Always call hooks at the top level
  const config = useMemo(
    () => ({
      // Use environment-specific SDK key, fallback to development key
      sdkKey: process.env.GATSBY_VWO_SDK_KEY || '6dc93857b49c1d9240276b16751c8e9a',
      accountId: 1123194,
      // add other config if required by your SDK version
    }),
    []
  );
  
  // Use bypass provider for SSR or non-whitelisted properties
  const shouldBypass = typeof window === "undefined" || !isWhitelisted;
  
  if (shouldBypass) {
    if (DEBUG) {
      const reason = typeof window === "undefined" ? "SSR" : "not whitelisted";
      console.log(`🎯 VWO: Using bypass provider (${reason})`, {
        property: pageContext?.slug || 'unknown',
        isVWOEnabled: pageContext?.isVWOEnabled,
        isSSR: typeof window === "undefined"
      });
    }
    return <VWOBypassProvider pageContext={pageContext}>{children}</VWOBypassProvider>;
  }

  // Debug VWO FME configuration in development
  if (DEBUG) {
    console.log("🎯 VWO FME Config:", {
      sdkKey: config.sdkKey,
      accountId: config.accountId,
      isWhitelisted: isWhitelisted,
      pageSlug: pageContext?.slug
    });
  }

  return (
    <VWOFMEProvider config={config}>
      <VWOApplyTestProvider>{children}</VWOApplyTestProvider>
    </VWOFMEProvider>
  );
};

// --- Hooks -------------------------------------------------------------------

export const useVWOApplyTest = () => {
  const ctx = useContext(VWOApplyContext);
  if (!ctx) {
    if (DEBUG) console.warn("useVWOApplyTest must be used within a VWOProvider");
    return {
      variation: "not_in_test",
      flagEnabled: false,
      showInterstitial: false,
      ready: true,
      isLoading: false,
      error: "Context not found",
    };
  }
  return ctx;
};

// Legacy alias
export const useVWO = () => useVWOApplyTest();

// Export useTrackEvent hook for event tracking
export { useTrackEvent };

export default VWOApplyContext;
